package com.tregz.design.motion

import androidx.compose.animation.core.FiniteAnimationSpec
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.spring
import androidx.compose.runtime.Composable
import androidx.compose.ui.unit.Dp

/**
 * The Material 3 Expressive motion springs, which the JetBrains Material 3 artifact keeps internal.
 *
 * Spatial specs move things around and may overshoot; effects specs fade and recolor and must not.
 * Each takes [reduced] (see [isReducedMotion]) and then snaps instead of animating.
 *
 * The generic `<T>` functions default [reduced] to false for source compatibility; prefer the typed
 * `@Composable` variants, which default it from [isReducedMotion] and never overshoot below zero.
 */
object MotionSpring {

    private const val SPATIAL_DAMPING = 0.8f
    private const val SPATIAL_STIFFNESS = 380f
    private const val FAST_DAMPING = 0.6f
    private const val FAST_STIFFNESS = 800f
    private const val SLOW_STIFFNESS = 200f
    private const val EFFECTS_DAMPING = 1f
    private const val EFFECTS_STIFFNESS = 1600f
    private const val EFFECTS_SLOW_STIFFNESS = 800f

    fun <T> spatial(reduced: Boolean = false): FiniteAnimationSpec<T> =
        if (reduced) snap() else spring(dampingRatio = SPATIAL_DAMPING, stiffness = SPATIAL_STIFFNESS)

    fun <T> spatialFast(reduced: Boolean = false): FiniteAnimationSpec<T> =
        if (reduced) snap() else spring(dampingRatio = FAST_DAMPING, stiffness = FAST_STIFFNESS)

    fun <T> spatialSlow(reduced: Boolean = false): FiniteAnimationSpec<T> =
        if (reduced) snap() else spring(dampingRatio = SPATIAL_DAMPING, stiffness = SLOW_STIFFNESS)

    fun <T> effects(reduced: Boolean = false): FiniteAnimationSpec<T> =
        if (reduced) snap() else spring(dampingRatio = EFFECTS_DAMPING, stiffness = EFFECTS_STIFFNESS)

    fun <T> effectsSlow(reduced: Boolean = false): FiniteAnimationSpec<T> =
        if (reduced) snap() else spring(dampingRatio = EFFECTS_DAMPING, stiffness = EFFECTS_SLOW_STIFFNESS)

    /**
     * A size or offset spring that is critically damped, so a Dp that must stay at or above zero
     * (a size, a padding) never overshoots below it.
     */
    @Composable
    fun spatialDp(reduced: Boolean = isReducedMotion()): FiniteAnimationSpec<Dp> =
        if (reduced) snap() else spring(dampingRatio = EFFECTS_DAMPING, stiffness = SPATIAL_STIFFNESS)

    /** A critically damped Float spring for scale and other non-negative spatial values. */
    @Composable
    fun spatialFloat(reduced: Boolean = isReducedMotion()): FiniteAnimationSpec<Float> =
        if (reduced) snap() else spring(dampingRatio = EFFECTS_DAMPING, stiffness = SPATIAL_STIFFNESS)

    /** An alpha spring: no overshoot, so the value stays inside 0..1 (still clamp with [alphaOf]). */
    @Composable
    fun alpha(reduced: Boolean = isReducedMotion()): FiniteAnimationSpec<Float> =
        if (reduced) snap() else spring(dampingRatio = EFFECTS_DAMPING, stiffness = EFFECTS_STIFFNESS)

    /** Clamps an animated alpha into 0..1. */
    fun alphaOf(value: Float): Float = if (value.isNaN()) 0f else value.coerceIn(0f, 1f)
}
